import React, { useState } from 'react';
import { Casa } from '../../types';
import { useTareas } from '../../context/TareasContext';
import { useAuth } from '../../context/AuthContext';
import { nombreDeUsuario } from '../../utils/usuario';
import { UserPlus } from 'lucide-react';
import { MensajeError, claseInput } from './ui';

/**
 * Primer paso de quien entra en una casa con una invitación: decir quién es.
 * Si ya le habían apuntado por su nombre, lo reclama y hereda sus tareas; si
 * no, se añade como persona nueva. Igual que en A medias.
 */
export const ElegirParticipanteCasa: React.FC<{ casa: Casa }> = ({ casa }) => {
  const { reclamarParticipante, apuntarmeComoNuevo } = useTareas();
  const { user } = useAuth();
  const [nombre, setNombre] = useState(nombreDeUsuario(user));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const libres = casa.participantes.filter(p => !p.uid && p.activo);

  const ejecutar = async (fn: () => Promise<void>) => {
    setGuardando(true);
    setError('');
    try { await fn(); } catch (e) {
      console.error(e);
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  const nuevo = (e: React.FormEvent) => {
    e.preventDefault();
    const n = nombre.trim();
    if (!n) { setError('Escribe tu nombre.'); return; }
    if (casa.participantes.some(p => p.nombre.trim().toLowerCase() === n.toLowerCase())) {
      setError(`Ya hay alguien llamado «${n}». Si eres tú, elígelo arriba; si no, usa otro nombre.`);
      return;
    }
    ejecutar(() => apuntarmeComoNuevo(casa.id, n));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-lumbre-300 dark:border-lumbre-700 p-5 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          {casa.icono} ¿Quién eres en «{casa.nombre}»?
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          Así sabremos qué tareas te tocan a ti.
        </p>
      </div>
      <MensajeError texto={error} />

      {libres.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Si ya te habían apuntado, elige tu nombre:</p>
          <div className="flex flex-wrap gap-2">
            {libres.map(p => (
              <button key={p.id} disabled={guardando}
                onClick={() => ejecutar(() => reclamarParticipante(casa.id, p.id))}
                className="px-4 py-2 rounded-lg border border-lumbre-300 dark:border-lumbre-700 text-sm font-semibold
                           text-lumbre-800 dark:text-lumbre-300 hover:bg-lumbre-50 dark:hover:bg-lumbre-900/30
                           transition disabled:opacity-60">
                Soy {p.nombre}
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={nuevo}>
        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
          {libres.length > 0 ? 'Si no estás en la lista, apúntate:' : 'Apúntate con tu nombre:'}
        </p>
        <div className="flex gap-2">
          <input type="text" value={nombre} onChange={e => setNombre(e.target.value)} className={claseInput} />
          <button type="submit" disabled={guardando}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-lumbre-600 hover:bg-lumbre-700
                       text-white text-sm font-semibold transition disabled:opacity-60">
            <UserPlus className="w-4 h-4" /> Soy nuevo
          </button>
        </div>
      </form>
    </div>
  );
};
